import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2, ShieldCheck, Zap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API, BRAND } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { useSeo } from "@/lib/seo";

export default function StaffLogin() {
  useSeo({ title: "Staff Login — Flow Tweaks", path: "/staff/login", noindex: true });

  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/staff/login`, { username, password });
      setAuth(res.data.token, "staff");
      toast.success("Welcome, " + res.data.user.username);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col" data-testid="staff-login-page">
      <header className="border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center">
          <button onClick={() => navigate("/")} className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-500 fill-orange-500" />
            <span className="font-black tracking-[0.18em] text-sm uppercase" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              {BRAND.name}
            </span>
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md border border-orange-500/30 bg-[#0a0a0a] p-8"
        >
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-5 h-5 text-orange-500" />
            <p className="text-orange-500 text-[10px] tracking-[0.3em] uppercase font-bold">Staff Access</p>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tighter mb-2" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            Tweaker Login
          </h1>
          <p className="text-zinc-400 text-sm mb-6">Authorized personnel only.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">Username</Label>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-2 bg-black border-white/10 rounded-none focus-visible:ring-orange-500"
                data-testid="staff-username-input"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-black border-white/10 rounded-none focus-visible:ring-orange-500"
                data-testid="staff-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-400 text-black font-black uppercase tracking-[0.2em] text-xs py-4 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="staff-login-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Sign In
            </button>
          </form>
        </motion.div>
      </main>
    </div>
  );
}

